import { useEffect, useRef } from "react";

// LDF: Wischgesten auf dem Handy. Nach links wischen = weiter (onLeft), nach rechts = zurück (onRight).
// Ignoriert Gesten in Eingabefeldern, in seitlich scrollbaren Bereichen (z. B. Chips, Board), bei offenen Fenstern
// und in Elementen mit data-no-swipe.

const isHorizontallyScrollable = (el: HTMLElement | null) => {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 2) return true;
  }
  return false;
};

export const useSwipe = ({
  onLeft,
  onRight,
  enabled = true,
}: {
  onLeft?: () => void;
  onRight?: () => void;
  enabled?: boolean;
}) => {
  const handlers = useRef({ onLeft, onRight });
  handlers.current = { onLeft, onRight };

  useEffect(() => {
    if (!enabled) return;
    let start: { x: number; y: number; t: number } | null = null;

    const onStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (e.touches.length !== 1) return (start = null);
      if (target?.closest("input, textarea, select, [contenteditable=true], [data-no-swipe]")) return (start = null);
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"]')) return (start = null);
      if (isHorizontallyScrollable(target)) return (start = null);
      start = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
    };

    const onEnd = (e: TouchEvent) => {
      if (!start) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const elapsed = Date.now() - start.t;
      start = null;
      // deutlich waagerecht, schnell genug, nicht vom Bildschirmrand (iOS-Zurückgeste)
      if (Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) * 0.6 || elapsed > 700) return;
      if (dx < 0) handlers.current.onLeft?.();
      else handlers.current.onRight?.();
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
    };
  }, [enabled]);
};
